import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutGroup } from "framer-motion";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { stagesFor } from "@/lib/charts";
import { moveWithUndo } from "@/lib/moveStage";
import { SkeletonRows } from "@/components/app/Common";
import { KanbanColumn } from "@/pages/job/KanbanColumn";
import { cn } from "@/lib/utils";
import { AddCandidateButton } from "@/components/intake/AddCandidateButton";

const CATS = ["Strong Fit", "Potential Fit", "Needs Review", "Not a Fit", "Not a Fit – Knockout"];

function useEdges(ref, deps) {
  const [edges, setEdges] = useState({ l: false, r: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const upd = () => setEdges({ l: el.scrollLeft > 4, r: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
    upd();
    el.addEventListener("scroll", upd, { passive: true });
    window.addEventListener("resize", upd);
    return () => { el.removeEventListener("scroll", upd); window.removeEventListener("resize", upd); };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return edges;
}

function useCollapsed(jobId) {
  const key = `tl_kanban_collapsed_${jobId}`;
  const [set, setSet] = useState(() => { const s = localStorage.getItem(key); return s ? new Set(JSON.parse(s)) : null; });
  const resolve = (emptyKeys) => set || new Set(emptyKeys);
  const toggle = (k, emptyKeys) => { const n = new Set(resolve(emptyKeys)); n.has(k) ? n.delete(k) : n.add(k); localStorage.setItem(key, JSON.stringify([...n])); setSet(n); };
  return [resolve, toggle];
}

export function PipelineTab({ job }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = STAFF.includes(user.role);
  const [group, setGroup] = useState("stage");
  const [over, setOver] = useState(null);
  const [resolveCollapsed, toggleCol] = useCollapsed(job.id);
  const scroller = useRef(null);
  const { data, isLoading } = useQuery({ queryKey: ["ranked", job.id], queryFn: () => get(`/jobs/${job.id}/ranked`) });
  const stages = stagesFor(job);
  const rows = data?.rows || [];
  const columns = group === "stage" ? stages.map(([k, l]) => [k, l, rows.filter((r) => r.stage === k)]) : CATS.map((c) => [c, c, rows.filter((r) => r.category === c)]);
  const emptyKeys = columns.filter((c) => !c[2].length).map((c) => c[0]);
  const collapsed = resolveCollapsed(emptyKeys);
  const toggle = (k) => toggleCol(k, emptyKeys);
  const edges = useEdges(scroller, [data, group, collapsed.size]);
  const onMove = (ids, stage) => moveWithUndo(ids, stage, () => qc.invalidateQueries({ queryKey: ["ranked", job.id] }));
  if (isLoading) return <SkeletonRows rows={6} />;
  const dropProps = (k) => ({
    onDragOver: (e) => { if (group === "stage" && canEdit) { e.preventDefault(); setOver(k); } },
    onDragLeave: () => setOver(null),
    onDrop: (e) => { e.preventDefault(); setOver(null); const id = e.dataTransfer.getData("text/plain"); if (id && rows.find((r) => r.application_id === id)?.stage !== k) onMove([id], k); },
  });

  return (
    <div className="space-y-4" data-testid="job-pipeline-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit rounded-[5px] border border-line bg-white p-0.5">
          {[["stage", "By stage"], ["category", "By category"]].map(([k, l]) => (
            <button key={k} onClick={() => setGroup(k)} data-testid={`pipeline-group-${k}`} className={cn("rounded-md px-3 py-1 text-sm transition-colors duration-150", group === k ? "bg-brand-tint font-medium text-brand" : "text-ink-secondary hover:text-ink")}>{l}</button>
          ))}
        </div>
        <AddCandidateButton jobId={job.id} size="sm" testId="pipeline-add-candidate-btn" />
      </div>
      <div className="relative">
        <div className={cn("pointer-events-none absolute inset-y-0 left-0 z-10 w-px bg-line-strong transition-opacity duration-200", edges.l ? "opacity-100" : "opacity-0")} data-testid="kanban-fade-left" />
        <div className={cn("pointer-events-none absolute inset-y-0 right-0 z-10 w-px bg-line-strong transition-opacity duration-200", edges.r ? "opacity-100" : "opacity-0")} data-testid="kanban-fade-right" />
        <LayoutGroup id={`kanban-${job.id}`}>
          <div ref={scroller} className="flex snap-x snap-mandatory items-start gap-2 overflow-x-auto pb-4" data-tour="pt-kanban-board" data-testid="kanban-board">
            {columns.map(([k, label, items]) => (
              <KanbanColumn key={k} k={k} label={label} items={items} collapsed={collapsed.has(k)} onToggle={() => toggle(k)} over={over === k}
                dropProps={dropProps(k)} cardProps={{ stages, canEdit: canEdit && group === "stage", onMove }} />
            ))}
          </div>
        </LayoutGroup>
      </div>
    </div>
  );
}
